import { useCallback, useEffect, useState } from "react";
import { addDays, parseISO } from "date-fns";
import { ChevronLeft, ChevronRight, Copy, Save, Send, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { mondayOf, ymd, fmt, hoursOf } from "@/lib/dates";
import { ROSTER_STATUS } from "@/lib/colors";
import { PageHeader, Pill } from "@/components/common";
import { RosterEditor, toPayload } from "@/components/roster/RosterEditor";
import { RosterTemplates } from "@/components/roster/RosterTemplates";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const ChangesBanner = ({ changes }) => (
  <div className="rounded-2xl border border-orange-300 bg-orange-50 px-4 py-3 mb-4" data-testid="roster-changes-banner">
    <div className="flex items-center gap-2 font-bold text-orange-900"><AlertTriangle className="w-4 h-4" /> Your supervisor changed this schedule</div>
    <ul className="mt-2 space-y-1 text-sm text-orange-900/90 list-disc pl-6">
      {changes.map((c, i) => <li key={i} data-testid={`roster-change-${i}`}>{c}</li>)}
    </ul>
  </div>
);

export default function RosterBuilder() {
  const thisMonday = ymd(mondayOf(new Date()));
  const [week, setWeek] = useState(thisMonday);
  const [roster, setRoster] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [offDays, setOffDays] = useState([]);

  const load = useCallback(async () => {
    setRoster(null);
    const [{ data }, { data: hols }] = await Promise.all([api.get("/rosters/mine", { params: { week_start: week } }), api.get("/holidays")]);
    setRoster(data);
    setShifts(data.shifts);
    setDirty(false);
    const days = Array.from({ length: 7 }, (_, i) => ymd(addDays(parseISO(week), i)));
    setOffDays(days.filter((d) => hols.some((h) => h.status === "approved" && h.start_date <= d && h.end_date >= d)));
  }, [week]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      const { data } = await fn();
      setRoster(data);
      setShifts(data.shifts);
      setDirty(false);
      toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const save = () => run(() => api.put("/rosters/mine", { week_start: week, shifts: toPayload(shifts) }), "Draft saved");
  const copy = () => run(() => api.post("/rosters/mine/copy-last", { week_start: week }), "Copied last week's schedule");
  const applyTemplate = (id) => run(() => api.post("/rosters/mine/apply-template", { week_start: week, template_id: id }), "Template applied as draft");
  const submit = () =>
    run(async () => {
      if (dirty || roster.status === "new") await api.put("/rosters/mine", { week_start: week, shifts: toPayload(shifts) });
      return api.post("/rosters/mine/submit", { week_start: week });
    }, "Schedule submitted to your supervisor");

  const st = roster ? ROSTER_STATUS[roster.status] : null;
  const total = shifts.reduce((n, s) => n + hoursOf(s), 0);

  return (
    <div>
      <PageHeader title="Schedule builder" subtitle="Plan your week: days, hours, office or remote. Then send it to your supervisor." />
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="rounded-full" disabled={week <= thisMonday} onClick={() => setWeek(ymd(addDays(parseISO(week), -7)))} data-testid="roster-prev-week-btn"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="icon" className="rounded-full" onClick={() => setWeek(ymd(addDays(parseISO(week), 7)))} data-testid="roster-next-week-btn"><ChevronRight className="w-4 h-4" /></Button>
          <div className="ml-1">
            <div className="text-lg sm:text-xl font-bold text-slate-800" data-testid="roster-week-label">Week of {fmt(week, "d MMM yyyy")}</div>
            <div className="text-xs text-slate-500">{week === thisMonday ? "This week" : "Upcoming week"} · {shifts.length} shifts · {total.toFixed(1)} h</div>
          </div>
          {st && <Pill cls={st.cls} testid="roster-status-badge" className="ml-2">{st.label}</Pill>}
        </div>
        <div className="flex flex-wrap gap-2">
          <RosterTemplates shifts={shifts} disabled={busy} onApply={applyTemplate} />
          <Button variant="outline" className="rounded-full" onClick={copy} disabled={busy} data-testid="roster-copy-last-week-btn"><Copy className="w-4 h-4 mr-1.5" /> Copy last week</Button>
          <Button variant="outline" className="rounded-full" onClick={save} disabled={busy || !dirty} data-testid="roster-save-draft-btn"><Save className="w-4 h-4 mr-1.5" /> Save draft</Button>
          <Button className="rounded-full" onClick={submit} disabled={busy || !shifts.length || (roster?.status === "submitted" && !dirty)} data-testid="roster-submit-btn"><Send className="w-4 h-4 mr-1.5" /> Submit</Button>
        </div>
      </div>
      {roster?.status === "changed" && roster.changes?.length > 0 && <ChangesBanner changes={roster.changes} />}
      {roster && ["submitted", "approved"].includes(roster.status) && dirty && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 mb-4" data-testid="roster-resubmit-warning">
          Saving changes moves this roster back to Draft — submit it again for approval.
        </div>
      )}
      {!roster ? <Skeleton className="h-[480px] rounded-2xl" /> : (
        <RosterEditor weekStart={week} shifts={shifts} offDays={offDays} disabled={busy} onChange={(s) => { setShifts(s); setDirty(true); }} />
      )}
    </div>
  );
}
